import type {
  DocumentNode,
  OperationVariables,
  QueryHookOptions,
  TypedDocumentNode,
} from '@apollo/client';
import { useQuery } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';

interface PaginationProps {
  step?: number;
  first?: number;
}

export const useInfiniteScroll = <TData = any, TVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  initData: TData,
  merge: (curr: TData) => (prev: TData) => TData,
  hasEnded: (data: TData) => boolean,
  options?: QueryHookOptions<TData, TVariables> | undefined,
  paginationOptions?: PaginationProps | undefined,
  network?: string,
) => {
  const [skip, setSkip] = useState(0);
  const [first, _] = useState(paginationOptions?.first ?? 20);
  const [ended, setEnded] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [fetchedData, setFetchedData] = useState<TData>(initData);

  const queryData = useQuery<TData, TVariables>(query, {
    ...options,
    fetchPolicy: 'no-cache',
    context: { network },
  });

  useEffect(() => {
    if (!queryData.data) {
      return;
    }

    setFetchedData(queryData.data);
    setSkip(0);
    setEnded(false);
  }, [queryData.data]);

  useEffect(() => {
    if (ended || !skip) {
      return;
    }

    void (async () => {
      const res = await queryData.fetchMore({
        variables: {
          skip,
        },
      });
      const newData = res?.data;
      if (!newData) {
        return;
      }

      if (hasEnded(newData as TData)) {
        setEnded(true);
        setSkip(0);
      } else {
        setFetchedData(merge(newData as TData));
      }
      setFetching(false);
    })();
  }, [skip]);

  useEffect(() => {
    if (!fetching) {
      return;
    }

    setSkip(prev => prev + first);
  }, [fetching]);

  const loader = useRef<HTMLDivElement | null>(null);

  const handleObserver = useCallback(
    (
      entries: {
        isIntersecting: boolean;
      }[],
    ) => {
      const target: { isIntersecting: boolean } = entries[0];
      if (target.isIntersecting) {
        setFetching(true);
      }
    },
    [],
  );

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: '20px',
      threshold: 0,
    };
    const observer = new IntersectionObserver(handleObserver, option);
    if (loader.current) {
      observer.observe(loader.current);
    }
  }, [handleObserver, queryData.loading]);

  return {
    ...queryData,
    fetchedData,
    loader,
  };
};
